import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAllFootage, useTrips, useCollection, useMovie, updateMovie, updateProofSheet, queueSheetColorProofs, queueSdkPreview, isTrashed } from '../data/firestore';
import PhotoLightbox from '../components/PhotoLightbox';
import CropBox from '../components/CropBox';
import { ClipProofsBody } from './ClipProofs';
import { bust } from '../utils/cacheBust';
import { fmtTime, masterSegments, clipReframe } from '../utils/movie';
import { centerAt, scaleAt } from '../utils/social';

// Contact-sheet view for "proof sheet" frame grabs: a clip's stills laid out 3
// large per row, each labeled with its source frame, in clip (frame) order — so
// you can scan a whole wave at once and pick the hero frame / dial the grade.
// Proof stills are photo assets tagged `*-proof-sheet` (e.g. laurent-proof-sheet)
// and carry a `proofFrame` + `proofClip`. They live alongside everything else on
// the Photos page; this page just gives them a dedicated, legible layout.
const sheetImg = (p) =>
  bust(p.derivatives?.magazine || p.displayUrl || p.derivatives?.fulljpeg || p.thumbUrl || '', p);
const isBaking = (p) => p.stillGrab?.status === 'queued' || p.stillGrab?.status === 'rendering';
const isSheet = (a) =>
  !!a.proofSheet?.name ||
  (Array.isArray(a.tags) && a.tags.some((t) => t === 'proof-sheet' || t.endsWith('-proof-sheet')));
const frameOf = (p) =>
  Number.isFinite(p.proofFrame) ? p.proofFrame
    : Number.isFinite(p.stillGrab?.frameIndex) ? p.stillGrab.frameIndex : 0;

// Crop-ratio choices for a crop request. 'original' keeps the frame's own
// aspect (lockRatio 1 in normalized space).
const CROP_RATIOS = [
  ['original', 'Original', 0],
  ['16:9', '16:9', 16 / 9],
  ['4:3', '4:3', 4 / 3],
  ['1:1', '1:1', 1],
  ['4:5', '4:5', 4 / 5],
  ['9:16', '9:16', 9 / 16],
  ['2.39:1', '2.39:1', 2.39],
];

// ---- Color grade mode -------------------------------------------------------
// Six-up per RED clip frame: {color de-log, neutral de-log} × {bare, AI look 1,
// AI look 2}, decoded FRESH from the RAW negative by the frameProofs worker
// (kind 'sheet-color') — never resampled from the published bake, which already
// has a grade burned in. Non-RED clips get {source, +look1, +look2}. Picking a
// tile writes the choice onto the movie clip itself: `redDecode` (SDK de-log
// base + EV + tone map + roll-off — a PER-CLIP override of the movie-wide
// output.redPipeline) and, for a look tile, `grade` (the look's composed grade
// JSON, gradeLocked, provenance stamped). The bare de-log tiles are first-class
// picks: most clips ship without any grade on top.

const TONE_OPTS = [['', 'Stock'], ['none', 'None'], ['low', 'Low'], ['medium', 'Medium'], ['high', 'High']];
const ROLL_OPTS = [['', 'Stock'], ['none', 'None'], ['hard', 'Low (hard)'], ['medium', 'Medium'], ['soft', 'High (soft)'], ['verysoft', 'Very high']];

// Match a sheet row back to its movie clip (rows carry assetId + source window,
// not clipId) — mirrors movieClipFor in backend/api/frameProofs.js.
function sheetClipToMovieClip(movie, row) {
  const clips = movie?.clips || [];
  const byAsset = clips.filter((c) => row.assetId && c.assetId === row.assetId);
  if (byAsset.length === 1) return byAsset[0];
  if (byAsset.length > 1) {
    const mid = ((row.inSec || 0) + (row.outSec || 0)) / 2;
    const hit = byAsset.find((c) => mid >= (c.inSec || 0) - 0.5 && mid <= (c.outSec || 0) + 0.5);
    if (hit) return hit;
  }
  return clips[(row.idx || 1) - 1] || null;
}

const fmtEta = (s) => {
  if (s == null || !Number.isFinite(s)) return '';
  if (s < 60) return `${Math.max(1, Math.round(s))}s`;
  const m = Math.floor(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m ${String(Math.round(s % 60)).padStart(2, '0')}s`;
};

// Live chip for one clip set's render state (colorProofs.progress[idx], written
// by the worker every ~2.5s with a measured ETA). Makes baking-vs-done obvious.
function ClipProgressChip({ pg, running }) {
  if (!pg) return null;
  if (pg.state === 'error') {
    return <span className="cs-proof-status" style={{ color: '#f87171', borderColor: '#7a2a2a' }} title={pg.step}>✕ failed — {pg.step}</span>;
  }
  if (!running) return null;
  if (pg.state === 'running') {
    const pct = pg.total ? Math.min(99, Math.round((pg.done / pg.total) * 100)) : 0;
    return (
      <span className="cs-grab-livetag" title={`${pg.done}/${pg.total} tiles`}>
        ● {pg.step || 'rendering'} · {pct}%{pg.etaSec != null ? ` · ~${fmtEta(pg.etaSec)} left` : ''}
      </span>
    );
  }
  if (pg.state === 'queued') return <span className="cs-proof-status">queued</span>;
  if (pg.state === 'done') return <span className="cs-proof-status cs-proof-status-done">✓ done{pg.elapsedSec ? ` in ${fmtEta(pg.elapsedSec)}` : ''}</span>;
  return null;
}

function ColorModeBody({ sheet, movie, targetId, cols }) {
  const applyId = targetId || sheet.movieId;
  const [tweak, setTweak] = useState(null); // { idx, exposure, toneMap, rolloff }
  const [note, setNote] = useState(null); // { idx, msg | err }
  const [busy, setBusy] = useState(false);
  const [workers, setWorkers] = useState('task'); // 'task' (in-process pool) | 'fanout' (ephemeral Fargate)
  const [par, setPar] = useState(0); // in-task pool size; 0 = live-config default
  const [refine, setRefine] = useState(null); // { idx, base, look, text, count, mode }
  const cp = sheet.colorProofs;
  const running = cp && (cp.status === 'running' || cp.status === 'queued');

  // LIVE NEW-ROW FLAGS: the sheet doc streams via onSnapshot, so freshly
  // rendered looks appear the moment a worker lands them — this diffs each
  // clip's look names against what this session last saw and flags additions,
  // so you can visually confirm exactly where the new rows are. First
  // observation of a clip just records (no flag storm on page load).
  const prevLooksRef = React.useRef({}); // idx -> [names]
  const [fresh, setFresh] = useState({}); // idx -> { names: [..], at }
  React.useEffect(() => {
    const clips = cp?.clips || {};
    const additions = {};
    for (const [idx, d] of Object.entries(clips)) {
      const names = (d.looks || []).map((l) => l.name);
      const prev = prevLooksRef.current[idx];
      if (prev) {
        const added = names.filter((n) => !prev.includes(n));
        if (added.length) additions[idx] = added;
      }
      prevLooksRef.current[idx] = names;
    }
    if (Object.keys(additions).length) {
      setFresh((f) => {
        const next = { ...f };
        for (const [idx, added] of Object.entries(additions)) {
          next[idx] = { names: [...new Set([...(next[idx]?.names || []), ...added])], at: Date.now() };
        }
        return next;
      });
    }
  }, [cp?.clips]);
  const dismissFresh = (idx) => setFresh((f) => {
    const next = { ...f };
    delete next[idx];
    return next;
  });
  const jumpToClip = (idx) => {
    document.getElementById(`proof-clip-${idx}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // EVERY request from this page honors the Workers choice — including
  // single-clip re-renders (refine, SDK tweak, adopt-sweep): one clip fans out
  // as one runner. ~60s boot before first pixel, but the always-on box stays
  // free and cached de-log bases make the runner grade-only anyway.
  const generate = async (opts) => {
    setBusy(true);
    try {
      await queueSheetColorProofs(sheet.id, {
        ...(workers === 'fanout' ? { fanout: true } : (par ? { parallel: par } : {})),
        ...(opts || {}),
      });
    } finally {
      setBusy(false);
    }
  };

  const workersCtl = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <label className="cs-help" style={{ fontSize: 12 }} title={workers === 'fanout'
        ? 'Each clip set renders on its OWN ephemeral Fargate worker (8 vCPU, ~1 min spin-up, auto-stops when done). Applies to EVERY request on this page, including single-clip refines/tweaks (one runner). Cached de-log bases in S3 mean re-runs are grade-only — workers skip the RDC download entirely. Best for long movies / full re-generates / when the backend box is busy baking.'
        : 'All clip sets share the always-on backend box; this sets how many render at once there. Default (live config) is 3 — they share 8 vCPUs, so more isn\'t always faster. Fine for a few clips or cache-hit re-runs (no worker boot wait).'}>
        Workers
        <select className="cs-input cs-input-sm" value={workers} onChange={(e) => setWorkers(e.target.value)} style={{ width: 'auto', marginLeft: 6 }}>
          <option value="task">Backend box (shared)</option>
          <option value="fanout">Fargate fan-out — 1 worker per clip</option>
        </select>
      </label>
      {workers === 'task' && (
        <label className="cs-help" style={{ fontSize: 12 }} title="Pool size on the shared box. The per-clip ETAs will tell you if wider is actually faster.">
          × parallel
          <select className="cs-input cs-input-sm" value={par} onChange={(e) => setPar(Number(e.target.value))} style={{ width: 'auto', marginLeft: 6 }}>
            <option value={0}>Default</option>
            {[1, 2, 3, 4, 6, 8].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      )}
    </span>
  );

  // Iterate on a clip's current look: NL instruction → 3-6 AI variants (an
  // intensity ladder around "as asked") layered on the recipe, or a no-AI
  // strength sweep. Mode 'append' (default) ADDS the variants next to the
  // clip's existing look columns (cap 8); 'replace' swaps them all out. SDK
  // settings carry over so the bases are cache hits and only grades change.
  const submitRefine = async (c, data, strengths) => {
    const lk = refine.look ? (data.looks || []).find((l) => l.id === refine.look) : null;
    const toLk = refine.towards ? (data.looks || []).find((l) => l.id === refine.towards) : null;
    const mode = refine.mode || 'append';
    const cur = (data?.looks || []).map((l) => ({ name: l.name, why: l.why || '', ops: l.ops || [], grade: l.grade || null }));
    await generate({
      clipIdxs: [c.idx],
      ...(data?.sdk ? { sdk: { [String(c.idx)]: data.sdk } } : {}),
      ...(mode === 'append' && cur.length ? { looks: { [String(c.idx)]: cur } } : {}),
      refine: {
        clipIdx: c.idx,
        base: refine.base,
        name: lk ? lk.name : 'bare',
        grade: lk ? lk.grade || null : null,
        // Second anchor: with NO text → an exact blend ladder between the two
        // recipes; WITH text → the AI aims its variants in that direction.
        ...(toLk ? { towards: { name: toLk.name, grade: toLk.grade || null } } : {}),
        mode,
        count: Number(refine.count) || 3,
        ...(strengths ? { strengths } : refine.text.trim() ? { instruction: refine.text.trim() } : {}),
      },
    });
    setRefine(null);
  };

  // Open the refine panel anchored on a specific tile / look column.
  const openRefine = (c, { base, look } = {}) => setRefine({
    idx: c.idx,
    base: base || sheet.colorChoices?.[String(c.idx)]?.base || (c.red ? 'color' : 'src'),
    look: look !== undefined ? look : (sheet.colorChoices?.[String(c.idx)]?.look || ''),
    towards: '',
    text: '',
    count: 3,
    mode: 'append',
  });

  // Re-render ONE clip with new SDK settings, reusing its existing looks so the
  // grade columns stay comparable across SDK tweaks (no fresh AI roll).
  const rerenderClip = async (c, data) => {
    // Carry full grades through too — reference-recipe looks have no ops.
    const looks = (data?.looks || []).map((l) => ({ name: l.name, why: l.why || '', ops: l.ops || [], grade: l.grade || null }));
    await generate({
      clipIdxs: [c.idx],
      sdk: { [String(c.idx)]: { exposure: Number(tweak.exposure) || 0, toneMap: tweak.toneMap || '', rolloff: tweak.rolloff || '' } },
      ...(looks.length ? { looks: { [String(c.idx)]: looks } } : {}),
    });
    setTweak(null);
  };

  // Write a tile's choice onto the movie clip: SDK development (RED) and/or the
  // look's grade. Explicit user action — never silent (provenance stamped).
  const useTile = async (c, data, baseId, lookId) => {
    setNote(null);
    try {
      if (!movie) throw new Error('movie not loaded yet');
      const clip = sheetClipToMovieClip(movie, c);
      if (!clip) throw new Error('could not match this sheet clip to the current timeline');
      const patched = { ...clip };
      if (c.red) {
        // Tone map / roll-off shape the COLOR de-log only — a neutral tile IS
        // the pure colorimetric de-log, so the bake must not re-apply them.
        const neutral = baseId === 'neutral';
        patched.redDecode = {
          pipeline: neutral ? 'ipp2-neutral' : 'ipp2-color',
          exposure: data.sdk?.exposure || 0,
          toneMap: neutral ? '' : (data.sdk?.toneMap || ''),
          rolloff: neutral ? '' : (data.sdk?.rolloff || ''),
        };
      }
      const look = lookId ? (data.looks || []).find((l) => l.id === lookId) : null;
      if (look) {
        if (!look.grade) throw new Error('this look has no composed grade — re-generate the color proofs');
        patched.grade = look.grade;
        patched.useGrade = true;
        patched.gradeLocked = true;
        patched.gradeSource = { kind: 'proof-sheet-look', sheetId: sheet.id, look: look.name, at: Date.now() };
      } else {
        // Bare de-log/source pick: the SDK development IS the look.
        patched.useGrade = false;
      }
      const clips = (movie.clips || []).map((cl) => (cl.id === clip.id ? patched : cl));
      await updateMovie(applyId, { clips });
      await updateProofSheet(sheet.id, {
        colorChoices: { [String(c.idx)]: { base: baseId, look: lookId || '', at: Date.now() } },
      });
      // Also refresh the clip's SDK preview proxy so the EDITOR previews this
      // exact development (movie.sdkPreviews — the Color tab swaps it in).
      if (c.red) queueSdkPreview(applyId, clip.id).catch(() => {});
      const what = [
        c.red ? `${baseId === 'neutral' ? 'neutral' : 'color'} de-log${data.sdk?.exposure ? ` @ ${data.sdk.exposure > 0 ? '+' : ''}${data.sdk.exposure}EV` : ''}${data.sdk?.toneMap ? ` · tone ${data.sdk.toneMap}` : ''}${data.sdk?.rolloff ? ` · rolloff ${data.sdk.rolloff}` : ''}` : null,
        look ? `look “${look.name}”` : 'no grade on top',
      ].filter(Boolean).join(' + ');
      setNote({ idx: c.idx, msg: `Saved ${what} onto “${clip.label || `clip ${c.idx}`}” — re-publish to bake. An editor SDK preview is building too.` });
    } catch (err) {
      setNote({ idx: c.idx, err: err.message || String(err) });
    }
  };

  if (!cp) {
    return (
      <div className="cs-empty" style={{ padding: 28 }}>
        <p style={{ marginTop: 0 }}>
          No color proofs yet for this sheet. Generating decodes each RED clip's sampled frames
          straight from the RAW negative through BOTH SDK de-logs (color + neutral), asks the AI
          for two minimal looks per clip, and lays out every version of every frame. When a
          reference look is configured (config/grading → colorProofs.ref, a graded hero still),
          RED clips get extra columns rendering that recipe — full strength and softened. Non-RED
          clips get their ungraded source frame + the AI looks.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="cs-btn cs-btn-primary" disabled={busy} onClick={() => generate()}>
            {busy ? 'Queuing…' : 'Generate color proofs'}
          </button>
          {workersCtl}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {running ? (
          <span className="cs-grab-livetag">● {cp.status === 'queued' ? 'queued' : cp.current || 'rendering…'}</span>
        ) : (
          <span className="cs-help" style={{ fontSize: 12 }}>
            {cp.status === 'error' ? `Failed: ${cp.message || 'unknown error'}` : `Rendered ${cp.updatedAt ? new Date(cp.updatedAt).toLocaleString() : ''}`}
            {cp.status === 'ready' && cp.message ? ` — ${cp.message}` : ''}
          </span>
        )}
        <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy || running} onClick={() => generate()}>
          Re-generate all (fresh AI looks)
        </button>
        {workersCtl}
      </div>
      {Object.keys(fresh).length > 0 && (
        <div className="cs-proof-freshbar">
          <span style={{ fontWeight: 600 }}>✦ New looks landed:</span>
          {Object.entries(fresh)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([idx, f]) => (
              <button key={idx} className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => jumpToClip(idx)} title={f.names.join(', ')}>
                Clip {idx} +{f.names.length}
              </button>
            ))}
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            style={{ opacity: 0.7 }}
            onClick={() => setFresh({})}
          >
            clear
          </button>
        </div>
      )}
      {(sheet.clips || []).map((c) => {
        const data = cp.clips?.[String(c.idx)];
        const chosen = sheet.colorChoices?.[String(c.idx)];
        const bases = c.red
          ? [['color', 'Color de-log'], ['neutral', 'Neutral de-log']]
          : [['src', 'Source']];
        const looks = data?.looks || [];
        const sweep = cp.sweeps?.[String(c.idx)];
        const tweaking = tweak?.idx === c.idx;
        const freshHere = fresh[String(c.idx)];
        const isFreshLook = (name) => !!freshHere && freshHere.names.includes(name);
        return (
          <div key={c.idx} id={`proof-clip-${c.idx}`} style={{ marginBottom: 30 }}>
            <div className="cs-sheet-cliptitle" style={{ alignItems: 'center', gap: 10 }}>
              <h3 style={{ margin: 0 }}>
                Clip {c.idx}: {c.label}
                {c.red && <span className="cs-grab-livetag" style={{ marginLeft: 8 }}>RED</span>}
              </h3>
              {freshHere && (
                <button
                  className="cs-grab-livetag cs-proof-freshtag"
                  title={`New since you opened this page: ${freshHere.names.join(', ')} — click to dismiss`}
                  onClick={() => dismissFresh(String(c.idx))}
                >
                  ✦ {freshHere.names.length} new look{freshHere.names.length > 1 ? 's' : ''}
                </button>
              )}
              {data?.sdk && (data.sdk.exposure || data.sdk.toneMap || data.sdk.rolloff) ? (
                <span className="cs-help" style={{ fontSize: 12 }}>
                  SDK: {data.sdk.exposure ? `${data.sdk.exposure > 0 ? '+' : ''}${data.sdk.exposure}EV` : '0EV'}
                  {data.sdk.toneMap ? ` · tone ${data.sdk.toneMap}` : ''}
                  {data.sdk.rolloff ? ` · rolloff ${data.sdk.rolloff}` : ''}
                </span>
              ) : null}
              {data?.framed && (
                <span className="cs-grab-livetag" title="These tiles include the clip's saved crop keyframes — this is the framing the bake will produce">
                  ✂ crop baked in
                </span>
              )}
              <ClipProgressChip pg={cp.progress?.[String(c.idx)]} running={running} />
              {chosen && (
                <span className="cs-grab-livetag" title="Saved onto the movie clip">
                  ✓ using {chosen.base === 'neutral' ? 'neutral' : chosen.base === 'color' ? 'color' : 'source'}
                  {chosen.look ? ` + ${(looks.find((l) => l.id === chosen.look) || {}).name || chosen.look}` : ' (bare)'}
                </span>
              )}
              {(() => {
                // What the clip bakes with RIGHT NOW (live movie doc, not this
                // sheet's own choices — a fresh sheet must still show the
                // current edit so you can see what you're changing FROM).
                const liveClip = movie ? sheetClipToMovieClip(movie, c) : null;
                if (!liveClip) return null;
                const base = c.red
                  ? (liveClip.redDecode?.pipeline === 'ipp2-neutral' ? 'neutral de-log' : 'color de-log')
                  : 'source';
                const look = liveClip.useGrade && liveClip.grade
                  ? ` + ${liveClip.gradeSource?.look || 'custom grade'}`
                  : ' (bare — no look)';
                return (
                  <span className="cs-grab-livetag" style={{ borderColor: '#f0b35e', color: '#f0b35e' }}
                    title="The development + look this clip currently bakes with in the movie edit">
                    ● in movie: {base}{look}
                  </span>
                );
              })()}
              {c.red && !tweaking && (
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  disabled={running}
                  title="Dial log exposure, tone map and highlight roll-off for THIS clip, then re-render its tiles"
                  onClick={() => setTweak({
                    idx: c.idx,
                    exposure: data?.sdk?.exposure || 0,
                    toneMap: data?.sdk?.toneMap || '',
                    rolloff: data?.sdk?.rolloff || '',
                  })}
                >
                  SDK settings
                </button>
              )}
              {c.red && (
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  disabled={running || busy}
                  title="Render a 3×3 tone-map × roll-off grid of this clip's hero frame (color de-log at the current EV) — the fastest way to SEE the right pair"
                  onClick={() => generate({ sweep: { clipIdx: c.idx, base: 'ipp2-color', exposure: data?.sdk?.exposure || 0 } })}
                >
                  3×3 SDK sweep
                </button>
              )}
              {data && refine?.idx !== c.idx && (
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  disabled={running || busy}
                  title="Iterate on a look in plain language (“warmer highlights, less teal in the foam”) — 3-6 adjusted variants join (or replace) this clip's look columns. Tip: the ✨ on any tile opens this pre-aimed at that exact base + look."
                  onClick={() => openRefine(c, { look: chosen?.look || (data.looks?.[0]?.id || '') })}
                >
                  ✨ Refine look
                </button>
              )}
              {!data && !running && (
                <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy} onClick={() => generate({ clipIdxs: [c.idx] })}>
                  Render this clip
                </button>
              )}
            </div>
            {data && (
              <div className="cs-help" style={{ fontSize: 11, margin: '2px 0 4px' }}>
                One pick per clip: every frame below is a sample of the SAME clip, so “Use” saves that de-log + look onto the whole
                movie clip and choosing another tile replaces it. ✨ on a tile (or clicking a column name) iterates that look in plain language.
              </div>
            )}
            {tweaking && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '8px 0' }}>
                <label className="cs-help" style={{ fontSize: 12 }}>
                  Log exposure (EV)
                  <input
                    className="cs-input cs-input-sm"
                    type="number" step="0.25" min="-3" max="3"
                    value={tweak.exposure}
                    onChange={(e) => setTweak((tw) => ({ ...tw, exposure: e.target.value }))}
                    style={{ width: 80, marginLeft: 6 }}
                  />
                </label>
                <label className="cs-help" style={{ fontSize: 12 }}>
                  Tone map
                  <select className="cs-input cs-input-sm" value={tweak.toneMap} onChange={(e) => setTweak((tw) => ({ ...tw, toneMap: e.target.value }))} style={{ width: 'auto', marginLeft: 6 }}>
                    {TONE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <label className="cs-help" style={{ fontSize: 12 }}>
                  Highlight roll-off
                  <select className="cs-input cs-input-sm" value={tweak.rolloff} onChange={(e) => setTweak((tw) => ({ ...tw, rolloff: e.target.value }))} style={{ width: 'auto', marginLeft: 6 }}>
                    {ROLL_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy} onClick={() => rerenderClip(c, data)}>
                  Re-render clip
                </button>
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setTweak(null)}>Cancel</button>
                <span className="cs-help" style={{ fontSize: 11 }}>
                  Tone map + roll-off shape the COLOR row only — NEUTRAL is always the pure colorimetric de-log (that difference is the point of showing both). EV trims development and applies to both rows. Looks are reused, so columns stay comparable.
                </span>
              </div>
            )}
            {refine?.idx === c.idx && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '8px 0' }}>
                <label className="cs-help" style={{ fontSize: 12 }}>
                  From
                  {c.red ? (
                    <select className="cs-input cs-input-sm" value={refine.base} onChange={(e) => setRefine((r) => ({ ...r, base: e.target.value }))} style={{ width: 'auto', margin: '0 4px 0 6px' }}>
                      <option value="color">Color de-log</option>
                      <option value="neutral">Neutral de-log</option>
                    </select>
                  ) : <span style={{ margin: '0 4px 0 6px' }}>source</span>}
                  <select className="cs-input cs-input-sm" value={refine.look} onChange={(e) => setRefine((r) => ({ ...r, look: e.target.value }))} style={{ width: 'auto' }}>
                    <option value="">bare (no look)</option>
                    {looks.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </label>
                <label className="cs-help" style={{ fontSize: 12 }} title="Optional second anchor. With an empty text box: exact evenly-spaced blends between the two recipes when they share a processing profile — when they don't (e.g. an AI look vs the Tucker reference recipe), the AI looks at BOTH anchors rendered on the hero frame and designs perceptual in-between steps from the clean base instead (recipes from different pipelines can't be numerically lerped). With text: the AI aims its variants from the first look toward this one.">
                  towards
                  <select className="cs-input cs-input-sm" value={refine.towards || ''} onChange={(e) => setRefine((r) => ({ ...r, towards: e.target.value }))} style={{ width: 'auto', marginLeft: 6 }}>
                    <option value="">—</option>
                    {looks.filter((l) => l.id !== refine.look).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </label>
                <input
                  className="cs-input cs-input-sm"
                  placeholder={refine.towards ? 'optional — blank = exact blends between the two looks' : 'e.g. warmer highlights, less teal in the foam'}
                  value={refine.text}
                  onChange={(e) => setRefine((r) => ({ ...r, text: e.target.value }))}
                  style={{ flex: '1 1 260px', minWidth: 200 }}
                />
                <label className="cs-help" style={{ fontSize: 12 }} title="How many variants the AI renders — an intensity ladder from lightest to strongest around your literal request">
                  Variants
                  <select className="cs-input cs-input-sm" value={refine.count || 3} onChange={(e) => setRefine((r) => ({ ...r, count: Number(e.target.value) }))} style={{ width: 'auto', marginLeft: 6 }}>
                    {[3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <label className="cs-help" style={{ fontSize: 12 }} title="Add: new variants become EXTRA columns next to the current looks (up to 8 — oldest drop first). Replace: variants swap out ALL look columns (the old behavior).">
                  <select className="cs-input cs-input-sm" value={refine.mode || 'append'} onChange={(e) => setRefine((r) => ({ ...r, mode: e.target.value }))} style={{ width: 'auto' }}>
                    <option value="append">add alongside current looks</option>
                    <option value="replace">replace all look columns</option>
                  </select>
                </label>
                <button
                  className="cs-btn cs-btn-primary cs-btn-sm"
                  disabled={busy || (!refine.text.trim() && !refine.towards)}
                  title={refine.towards && !refine.text.trim()
                    ? 'Blends between the two selected looks — exact keyframe math when they share a profile, or an AI-designed perceptual bridge when their pipelines differ'
                    : 'AI reads the request + the current look on this clip\'s hero frame (it also knows every existing look\'s numeric recipe, so reference them by name), and renders the variants as an intensity ladder'}
                  onClick={() => submitRefine(c, data)}
                >
                  {refine.towards && !refine.text.trim() ? `Blend → ${refine.count || 3} between` : `Refine → ${refine.count || 3} variants`}
                </button>
                <button
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  disabled={busy || !refine.look}
                  title="No AI: the selected look's recipe at 50% / 75% / 125% strength — the quick manual intensity lever"
                  onClick={() => submitRefine(c, data, [0.5, 0.75, 1.25])}
                >
                  Strength sweep 50/75/125%
                </button>
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setRefine(null)}>Cancel</button>
                <span className="cs-help" style={{ fontSize: 11, flexBasis: '100%' }}>
                  “Add” keeps the current looks and appends the variants — past 6 they wrap into extra rows on this sheet; “replace” swaps them all.
                  Pick a “towards” look for in-between blends (exact math when the text box is empty). The AI knows every existing look's numeric
                  recipe, so the text box understands “between Filmic Neutral and Teal Aqua” or “more like Tucker v21 but cooler”.
                  SDK development stays as-is either way, so bases re-render from cache. Manual de-log controls (EV / tone map / roll-off)
                  are under “SDK settings”; full slider-level grading lives in the movie editor's Color tab after you “Use” a tile.
                </span>
              </div>
            )}
            {data?.refinedFrom && (data.refinedFrom.instruction || data.refinedFrom.strengths || data.refinedFrom.towards) && (
              <div className="cs-help" style={{ fontSize: 12, margin: '6px 0' }}>
                ↺ refined from <b>{data.refinedFrom.name || 'bare'}</b> ({data.refinedFrom.base || 'color'})
                {data.refinedFrom.towards ? <> towards <b>{data.refinedFrom.towards}</b></> : null}
                {data.refinedFrom.instruction ? <> — “{data.refinedFrom.instruction}”</> : data.refinedFrom.strengths ? ' — strength sweep' : ' — exact blends'}
                {data.refinedFrom.mode === 'append' ? ' · added alongside' : ' · replaced columns'}
              </div>
            )}
            {note?.idx === c.idx && (
              <div className="cs-help" style={{ fontSize: 12, margin: '6px 0', color: note.err ? 'var(--danger, #f87171)' : 'var(--surf, #4fd1c5)' }}>
                {note.err || note.msg}
              </div>
            )}
            {looks.length > 0 && (
              <div className="cs-help" style={{ fontSize: 12, margin: '6px 0' }}>
                {looks.map((l) => (
                  <span key={l.id} style={{ marginRight: 16 }}>
                    <b>{l.name}</b>{l.why ? ` — ${l.why}` : ''}
                  </span>
                ))}
                {data?.looksSource === 'fallback' && <span style={{ opacity: 0.7 }}>(preset looks — AI unavailable)</span>}
              </div>
            )}
            {!data ? (
              <div className="cs-help" style={{ fontSize: 12, margin: '6px 0' }}>
                {running ? 'Waiting for the worker…' : 'Not rendered yet.'}
              </div>
            ) : (
              (() => {
                // Columns = grade variant (bare + each look); rows = de-log base.
                // Labels live OUTSIDE the tiles: a header row per frame block +
                // a slim vertical gutter naming each base row — nothing covers
                // the image. Past the page's Columns setting the looks WRAP into
                // extra banks of rows (bare leads the first bank only) — columns
                // never shrink below readable, rows just stack.
                const allCols = [['', 'bare'], ...looks.map((l) => [l.id, l.name])];
                const MAX_COLS = Math.min(7, Math.max(1, Number(cols) || 6));
                const banks = [];
                for (let i = 0; i < allCols.length; i += MAX_COLS) banks.push(allCols.slice(i, i + MAX_COLS));
                const baseTag = { color: 'COLOR', neutral: 'NEUTRAL', src: 'SOURCE' };
                return (data.frames || []).map((f) => (
                  <div key={f.t} style={{ margin: '12px 0' }}>
                    <div className="cs-help" style={{ fontSize: 11, marginBottom: 4 }}>
                      movie {fmtTime(f.t)} · source {fmtTime(f.srcSec)}
                    </div>
                    {banks.map((cols, bi) => {
                      const gridCols = `20px repeat(${MAX_COLS}, 1fr)`;
                      return (
                        <div key={bi} style={{ marginBottom: bi < banks.length - 1 ? 10 : 0 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 6, marginBottom: 3 }}>
                            <div />
                            {cols.map(([lid, label]) => {
                              const why = lid ? (looks.find((l) => l.id === lid) || {}).why : 'de-log only, no grade on top';
                              const isNew = lid && isFreshLook(label);
                              return (
                                <div
                                  key={lid || 'bare'}
                                  className={`cs-proofcol-head cs-proofcol-click${isNew ? ' cs-proofcol-new' : ''}`}
                                  title={`${isNew ? 'NEW — just rendered. ' : ''}${why || label}${lid ? ' — click to refine this look in plain language' : ' — click to build a look from the bare base'}`}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => !running && !busy && openRefine(c, { look: lid })}
                                  onKeyDown={(e) => { if (e.key === 'Enter' && !running && !busy) openRefine(c, { look: lid }); }}
                                >
                                  {isNew ? '✦ ' : ''}{label}
                                </div>
                              );
                            })}
                            {cols.length < MAX_COLS && Array.from({ length: MAX_COLS - cols.length }, (_, i) => <div key={`pad${i}`} />)}
                          </div>
                          {bases.map(([bid, blabel]) => (
                            <div key={bid} style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 6, marginBottom: 6 }}>
                              <div className="cs-proofrow-tag" title={blabel}>{baseTag[bid] || bid}</div>
                              {cols.map(([lid, label]) => {
                                const url = f.tiles?.[lid ? `${bid}_${lid}` : bid];
                                const isChosen = chosen && chosen.base === bid && (chosen.look || '') === lid;
                                const isNew = lid && isFreshLook(label);
                                if (!url) return <div key={lid || 'bare'} className="cs-empty" style={{ aspectRatio: '16/9' }} />;
                                return (
                                  <div
                                    key={lid || 'bare'}
                                    style={{
                                      position: 'relative', lineHeight: 0, borderRadius: 4, overflow: 'hidden',
                                      outline: isChosen ? '2px solid var(--surf, #4fd1c5)' : isNew ? '2px solid rgba(255, 209, 102, 0.75)' : 'none',
                                    }}
                                  >
                                    <a href={url} target="_blank" rel="noreferrer" title={`${blabel}${lid ? ` + ${label}` : ' (bare)'} — open full size`}>
                                      <img src={url} alt={`${blabel} ${label}`} loading="lazy" style={{ width: '100%', display: 'block' }} />
                                    </a>
                                    <button
                                      className="cs-btn cs-btn-primary cs-btn-sm cs-prooftile-use"
                                      disabled={!movie}
                                      title={`Save ${blabel}${lid ? ` + ${label}` : ' (bare)'} onto the movie clip — one pick per clip, so this replaces any previous choice; the next bake decodes THIS clip exactly like this tile`}
                                      onClick={() => useTile(c, data, bid, lid)}
                                    >
                                      {isChosen ? '✓ using' : chosen ? 'Use instead' : 'Use'}
                                    </button>
                                    <button
                                      className="cs-btn cs-btn-ghost cs-btn-sm cs-prooftile-use cs-prooftile-refine"
                                      disabled={running || busy}
                                      title={`Iterate FROM this tile (${blabel}${lid ? ` + ${label}` : ', bare'}) — describe the change in plain language and get 3-6 variants`}
                                      onClick={() => openRefine(c, { base: bid, look: lid })}
                                    >
                                      ✨
                                    </button>
                                  </div>
                                );
                              })}
                              {cols.length < MAX_COLS && Array.from({ length: MAX_COLS - cols.length }, (_, i) => <div key={`pad${i}`} />)}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ));
              })()
            )}
            {sweep && (
              <div style={{ margin: '12px 0' }}>
                <div className="cs-help" style={{ fontSize: 12, marginBottom: 4 }}>
                  SDK sweep — {sweep.base === 'ipp2-neutral' ? 'neutral' : 'color'} de-log @ {sweep.exposure > 0 ? '+' : ''}{sweep.exposure || 0}EV,
                  hero frame @ source {fmtTime(sweep.sec)} · rows = tone map (low→high), columns = roll-off (low→high)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                  {(sweep.tiles || []).map((tl) => (
                    <div key={`${tl.toneMap}-${tl.rolloff}`} style={{ position: 'relative', lineHeight: 0, borderRadius: 4, overflow: 'hidden' }}>
                      <a href={tl.url} target="_blank" rel="noreferrer" title={`tone ${tl.toneMap} · rolloff ${tl.rolloff} — open full size`}>
                        <img src={tl.url} alt={`tone ${tl.toneMap} rolloff ${tl.rolloff}`} loading="lazy" style={{ width: '100%', display: 'block' }} />
                      </a>
                      <span
                        style={{
                          position: 'absolute', left: 4, bottom: 4, padding: '1px 6px',
                          background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, borderRadius: 3,
                        }}
                      >
                        tone {tl.toneMap} · roll {tl.rolloff}
                      </span>
                      <button
                        className="cs-btn cs-btn-primary cs-btn-sm"
                        style={{ position: 'absolute', top: 4, right: 4, padding: '2px 8px' }}
                        disabled={running || busy}
                        title="Adopt this tone map + roll-off for the clip and re-render its full tile grid"
                        onClick={async () => {
                          const looksReuse = (data?.looks || []).map((l) => ({ name: l.name, why: l.why || '', ops: l.ops || [] }));
                          await generate({
                            clipIdxs: [c.idx],
                            sdk: { [String(c.idx)]: { exposure: sweep.exposure || 0, toneMap: tl.toneMap, rolloff: tl.rolloff } },
                            ...(looksReuse.length ? { looks: { [String(c.idx)]: looksReuse } } : {}),
                          });
                        }}
                      >
                        Adopt
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// One page = one saved movie-bake proof sheet (Firestore `proofSheets` doc):
// per clip, a strip of frames sampled from the PUBLISHED bake itself, so what
// you see is exactly what the bake produced (delog, grade, reframe, crop).
// Frames open full-res in a new tab for pixel-peeping; feedback in plain
// language ("clip 3 too flat") maps back through clip idx + timestamp.
//
// "Adjust crop" per clip, two size modes:
//  • ONE size (default): the first box you draw sets the zoom (resizing any box
//    re-zooms them all — accidental per-frame size drift would make the bake
//    breathe); every other frame gets the same-size box you DRAG to tag center.
//  • ANIMATE size: each tagged frame keeps its OWN box size — these become zoom
//    keyframes (`zoomKeys`) on the movie clip, so the crop can start full frame
//    and smoothly punch in to a later keyframe (the bake eases size exactly
//    like the pan: same EMA, same output-fps sampling).
// Saving writes a `cropRequests` entry on the sheet doc — the marching orders
// for the next reframe pass, which turns the boxes into subject-centered
// waypoints + zoom keys (YOLO-assisted, keyframe-smoothed) on the movie clip.
function MovieSheetBody({ sheet }) {
  // Active crop-adjust session: one clip at a time.
  // { idx, ratio, animate, size: {w,h}|null (shared zoom),
  //   sizes: {frameIdx: {w,h}} (animate mode), centers: {frameIdx: {cx,cy}}, note }
  const [adjust, setAdjust] = useState(null);
  const [frameAspect, setFrameAspect] = useState(16 / 9); // measured from the images
  const [saving, setSaving] = useState(false);
  const [applyNote, setApplyNote] = useState(null); // { idx, msg | err }
  // 'crop' (bake frames + crop tags) | 'color' (de-log/look tiles). URL-backed
  // (?mode=color) so the editor's Color / Reframe tabs can deep-link straight
  // into the matching mode.
  const [params, setParams] = useSearchParams();
  const mode = params.get('mode') === 'color' ? 'color' : 'crop';
  const setMode = (m) => {
    const next = new URLSearchParams(params);
    if (m === 'color') next.set('mode', 'color');
    else next.delete('mode');
    setParams(next, { replace: true });
  };
  // Tiles-per-row zoom (?cols=1..7) — 1 = giant single-column pixel-peeping,
  // 7 = dense overview. 0/absent = the mode's default (crop 5, color 6).
  // URL-backed so a zoomed-in view survives reloads and can be shared.
  const colsChoice = Math.min(7, Math.max(0, Number(params.get('cols')) || 0));
  const setCols = (n) => {
    const next = new URLSearchParams(params);
    if (n > 0) next.set('cols', String(n));
    else next.delete('cols');
    setParams(next, { replace: true });
  };
  const cropCols = colsChoice || 5;
  // Apply target: normally the sheet's own movie, but a version/branch editor
  // links here with ?movie=<its id> (branches inherit the parent's sheets) —
  // applies must land on the movie you came FROM, never silently on the
  // sheet's owner. Valid because the fork shares clip ids + timeline.
  const applyTargetId = params.get('movie') || sheet?.movieId;
  const { doc: movie } = useMovie(applyTargetId);

  if (!sheet) return <div className="cs-empty">Proof sheet not found.</div>;
  const when = sheet.createdAt ? new Date(sheet.createdAt).toLocaleString() : '';

  // Push a saved crop request onto the movie clip itself: boxes → reframe
  // waypoints (centers) + zoom keyframes (sizes), in master time. The frames on
  // this sheet were sampled from the bake's master timeline, so their `t`
  // values line up with the CURRENT cut as long as the timeline hasn't been
  // re-trimmed since the bake — the segment match below guards against drift.
  const applyToMovie = async (c, saved) => {
    setApplyNote(null);
    try {
      if (!movie) throw new Error('movie not loaded yet');
      const boxes = saved.boxes || [];
      if (!boxes.length) throw new Error('no boxes in this crop request');
      const segs = masterSegments(movie.clips || []);
      const mid = (boxes[0].t + boxes[boxes.length - 1].t) / 2;
      const inWindow = segs.filter((s) => mid >= s.tileStart - 0.5 && mid <= s.tileEnd + 0.5);
      const seg = inWindow.find((s) => c.assetId && s.clip.assetId === c.assetId) || inWindow[0];
      if (!seg) throw new Error('could not match this sheet clip to the current timeline — the cut may have changed since the bake');
      // STALE-SHEET GUARD: every box must land inside the matched clip's
      // CURRENT window. A sheet baked on an older cut (clips re-trimmed since)
      // passes the loose mid-point match above but scatters keyframes into
      // neighboring clips — this silently ruined a bake once (July sheet
      // applied onto the Oct timeline: offsets up to 15s, one row landed on
      // the wrong clip entirely). Fail loudly instead.
      const outOfWindow = boxes.filter((b) => b.t < seg.tileStart - 0.5 || b.t > seg.tileEnd + 0.5);
      if (outOfWindow.length) {
        throw new Error(`${outOfWindow.length} of ${boxes.length} keyframes fall outside this clip's current timeline position — the cut changed since this sheet was generated. Regenerate the proof sheet from the latest bake, then re-tag.`);
      }
      const r3 = (v) => Math.round(v * 1000) / 1000;
      // The sheet's frames show what the bake RENDERED — if this clip had a
      // crop baked in, a box drawn on those frames is relative to that crop
      // window, so compose it back to full-frame coordinates before saving.
      // Prefer the sheet row's `bakedReframe` snapshot (what the frames were
      // ACTUALLY rendered with — newer sheets carry it); fall back to the
      // clip's live reframe for older sheets. Never compose through a reframe
      // that was already replaced by a previous Apply (that would double-apply).
      const old = c.bakedReframe !== undefined
        ? clipReframe({ reframe: c.bakedReframe })
        : clipReframe(saved.status === 'applied' ? null : seg.clip);
      const oldActive = old.manualCrop.scale < 0.999 || (old.zoomKeys || []).some((k) => k.scale < 0.999);
      const toAbs = (b) => {
        if (!oldActive) return b;
        const s = Math.max(0.25, Math.min(1, scaleAt(old.zoomKeys, old.manualCrop.scale, b.t)));
        const ctr = old.cropMode === 'manual' || !(old.waypoints || []).length
          ? { cx: old.manualCrop.cx, cy: old.manualCrop.cy }
          : centerAt(old.waypoints, b.t);
        const x0 = Math.max(0, Math.min(ctr.cx - s / 2, 1 - s));
        const y0 = Math.max(0, Math.min(ctr.cy - s / 2, 1 - s));
        return { t: b.t, cx: x0 + b.cx * s, cy: y0 + b.cy * s, scale: b.scale * s };
      };
      const abs = boxes.map(toAbs);
      const reframe = {
        ...old,
        cropMode: 'subject',
        // KEEP the clip's stabilization/subject track when it has one: the bake
        // anchors these keyframes INTO the dense track (keyframes win exactly
        // at their times, the track's motion fills between — see
        // applyDenseSubjectPaths). Nulling it here once dropped the dense
        // counter-wobble path on stabilized clips and baked 4–5 sparse boxes
        // verbatim: crop drift on every frame between keyframes.
        subjectTrackId: seg.clip.reframe?.subjectTrackId || null,
        waypoints: abs.map((b) => ({ t: r3(b.t), cx: r3(b.cx), cy: r3(b.cy) })),
        // Zoom keyframes: constant in one-size mode, animated in animate mode
        // (e.g. full frame → punch-in); the bake eases size like the pan.
        zoomKeys: abs.map((b) => ({ t: r3(b.t), scale: r3(Math.min(1, Math.max(0.25, b.scale))) })),
      };
      const clips = (movie.clips || []).map((cl) => (cl.id === seg.clip.id ? { ...cl, reframe } : cl));
      await updateMovie(applyTargetId, { clips });
      await updateProofSheet(sheet.id, {
        cropRequests: { [String(c.idx)]: { ...saved, status: 'applied', appliedAt: Date.now() } },
      });
      setApplyNote({ idx: c.idx, msg: `Applied ${boxes.length} crop keyframe${boxes.length === 1 ? '' : 's'} to “${seg.clip.label || `clip ${seg.idx + 1}`}” — re-publish to bake.` });
    } catch (err) {
      setApplyNote({ idx: c.idx, err: err.message || String(err) });
    }
  };

  const rNormFor = (ratio) => {
    const r = CROP_RATIOS.find(([id]) => id === ratio);
    if (!r || !r[2]) return 1; // 'original' = same shape as the frame
    return r[2] / frameAspect;
  };
  // Widest normalized box width the ratio allows inside the frame.
  const maxWFor = (ratio) => Math.min(1, rNormFor(ratio));

  const defaultSize = (ratio) => {
    const rn = rNormFor(ratio);
    const w = 0.65 * maxWFor(ratio);
    return { w, h: Math.min(1, w / rn) };
  };

  // The box size for frame j: its own size in animate mode, else the shared one.
  const sizeFor = (a, j) =>
    (a.animate && a.sizes?.[j]) || a.size || defaultSize(a.ratio);

  // The crop box for frame j, clamped inside the frame.
  const rectFor = (a, j) => {
    const size = sizeFor(a, j);
    const ctr = a.centers[j];
    if (!ctr) return null;
    const x = Math.max(0, Math.min(ctr.cx - size.w / 2, 1 - size.w));
    const y = Math.max(0, Math.min(ctr.cy - size.h / 2, 1 - size.h));
    return { x, y, w: size.w, h: size.h };
  };

  // The clip's CURRENT crop window at master time t (normalized, same-aspect
  // rect — how the bake interprets scale). Null when the clip has no crop.
  // Only meaningful on master-sourced sheets, whose frames are the FULL
  // original frame; on publish-sourced sheets the crop is baked into the
  // pixels and an overlay would be double-cropped.
  const fullFrameSheet = sheet.frameSource === 'master';
  const liveClipFor = (c) => (movie ? sheetClipToMovieClip(movie, c) : null);
  const liveCropBoxAt = (liveClip, t) => {
    if (!liveClip) return null;
    const r = clipReframe(liveClip);
    const hasPath = (r.waypoints || []).length || (r.zoomKeys || []).length || r.manualCrop.scale < 0.999;
    if (!hasPath) return null;
    const s = Math.max(0.25, Math.min(1, scaleAt(r.zoomKeys, r.manualCrop.scale, t)));
    const ctr = r.cropMode === 'manual' || !(r.waypoints || []).length
      ? { cx: r.manualCrop.cx, cy: r.manualCrop.cy }
      : centerAt(r.waypoints, t);
    if (s >= 0.999) return null;
    const x = Math.max(0, Math.min(ctr.cx - s / 2, 1 - s));
    const y = Math.max(0, Math.min(ctr.cy - s / 2, 1 - s));
    return { x, y, w: s, h: s };
  };

  const openAdjust = (c) => {
    const saved = sheet.cropRequests?.[String(c.idx)];
    const centers = {};
    const sizes = {};
    let size = null;
    if (!saved?.boxes && fullFrameSheet) {
      // No sheet boxes yet: seed the session from the clip's CURRENT crop path
      // so adjusting starts from what the movie bakes today (and "full frame"
      // is one corner-drag away), instead of re-drawing from scratch.
      const live = liveClipFor(c);
      (c.frames || []).forEach((f, j) => {
        const b = liveCropBoxAt(live, f.t);
        if (!b) return;
        if (!size) size = { w: b.w, h: b.h };
        sizes[j] = { w: b.w, h: b.h };
        centers[j] = { cx: b.x + b.w / 2, cy: b.y + b.h / 2 };
      });
      if (Object.keys(centers).length) {
        const vals = Object.values(sizes);
        const animate = vals.some((v) => Math.abs(v.w - vals[0].w) > 1e-3);
        setAdjust({ idx: c.idx, ratio: 'original', animate, size, sizes, centers, note: '' });
        return;
      }
    }
    if (saved?.boxes) {
      // Re-hydrate: centers per frame, plus per-frame sizes (animate) or the
      // shared size from the first box.
      saved.boxes.forEach((b) => {
        const j = (c.frames || []).findIndex((f) => Math.abs(f.t - b.t) < 0.05);
        if (j >= 0 && b.rect) {
          if (!size) size = { w: b.rect.w, h: b.rect.h };
          sizes[j] = { w: b.rect.w, h: b.rect.h };
          centers[j] = { cx: b.rect.x + b.rect.w / 2, cy: b.rect.y + b.rect.h / 2 };
        }
      });
    }
    setAdjust({
      idx: c.idx,
      ratio: saved?.ratio || 'original',
      animate: !!saved?.animate,
      size,
      sizes,
      centers,
      note: saved?.note || '',
    });
  };

  const saveAdjust = async (c) => {
    const { ratio, animate } = adjust;
    const maxW = maxWFor(ratio);
    const boxes = Object.keys(adjust.centers)
      .map(Number)
      .map((j) => {
        const r = rectFor(adjust, j);
        return {
          t: c.frames[j].t,
          frameUrl: c.frames[j].url,
          rect: r,
          cx: r.x + r.w / 2,
          cy: r.y + r.h / 2,
          // Zoom of THIS box (1 = full frame). Constant across boxes in one-size
          // mode; per-frame in animate mode → zoom keyframes on the clip.
          scale: Math.min(1, r.w / maxW),
        };
      })
      .sort((a, b) => a.t - b.t);
    setSaving(true);
    try {
      await updateProofSheet(sheet.id, {
        cropRequests: {
          [String(c.idx)]: {
            ratio,
            animate: !!animate,
            // Tightest zoom across the boxes (the widest punch-in the bake needs).
            scale: Math.min(...boxes.map((b) => b.scale)),
            boxes,
            note: adjust.note || '',
            requestedAt: Date.now(),
            status: 'requested',
          },
        },
      });
      setAdjust(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="cs-help" style={{ fontSize: 13, marginBottom: 10 }}>
        {sheet.movieTitle && (
          <>
            Movie: <a href={`/watch/${sheet.movieId}`}>{sheet.movieTitle}</a>
            {' · '}
          </>
        )}
        {when && `${when} · `}
        {sheet.note || ''}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className={`cs-btn cs-btn-sm ${mode === 'crop' ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
          title="Frames from the published bake — draw crop keyframes on them"
          onClick={() => setMode('crop')}
        >
          ✂ Crop
        </button>
        <button
          className={`cs-btn cs-btn-sm ${mode === 'color' ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
          title="Fresh RAW de-log tiles: color + neutral SDK development × AI looks, plus per-clip log EV / tone map / roll-off dial-in"
          onClick={() => setMode('color')}
        >
          🎨 Color
        </button>
        <label className="cs-help" style={{ fontSize: 12, marginLeft: 'auto' }} title="Tiles per row — 1 for giant pixel-peeping tiles, 7 for a dense overview. In color mode the look columns re-wrap into banks of this width.">
          Columns
          <select
            className="cs-input cs-input-sm"
            value={colsChoice || ''}
            onChange={(e) => setCols(Number(e.target.value) || 0)}
            style={{ width: 'auto', marginLeft: 6 }}
          >
            <option value="">Auto ({mode === 'color' ? 6 : 5})</option>
            {[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      {mode === 'color' && <ColorModeBody sheet={sheet} movie={movie} targetId={applyTargetId} cols={colsChoice || 6} />}
      {mode === 'color' ? null : (sheet.clips || []).map((c) => {
        const editing = adjust?.idx === c.idx;
        const saved = sheet.cropRequests?.[String(c.idx)];
        // A linked clip is the SAME continuous shot as its head clip (speed
        // change / micro-trim boundary): it inherits the head's crop so the
        // path stays continuous across the cut instead of jerking.
        const headSaved = c.linkedWith ? sheet.cropRequests?.[String(c.linkedWith)] : null;
        return (
          <div key={c.idx} style={{ marginBottom: 26 }}>
            <div className="cs-sheet-cliptitle" style={{ alignItems: 'center', gap: 10 }}>
              <h3 style={{ margin: 0 }}>
                Clip {c.idx}: {c.label}
                {c.red && <span className="cs-grab-livetag" style={{ marginLeft: 8 }}>RED</span>}
              </h3>
              <span className="cs-help" style={{ fontSize: 12 }}>{c.caption}</span>
              {c.linkedWith && (
                <span className="cs-grab-livetag" title="Same continuous shot — one crop path across the boundary, no jerk at the cut">
                  ⛓ same shot as clip {c.linkedWith}{headSaved ? ' · inherits its crop' : ' — crop it there'}
                </span>
              )}
              {saved && !editing && (
                <>
                  <span className="cs-grab-livetag" title={saved.note || ''}>
                    ✂ {saved.status === 'applied' ? 'crop applied' : 'crop requested'} · {saved.boxes?.length || 0} keyframe{(saved.boxes?.length || 0) === 1 ? '' : 's'} · {saved.ratio}
                    {saved.animate ? ' · animated size' : ''}
                  </span>
                  <button
                    className="cs-btn cs-btn-primary cs-btn-sm"
                    disabled={!movie}
                    title="Write these boxes onto the movie clip as reframe waypoints + zoom keyframes — then re-publish to bake"
                    onClick={() => applyToMovie(c, saved)}
                  >
                    {saved.status === 'applied' ? 'Re-apply to movie' : 'Apply to movie'}
                  </button>
                </>
              )}
              {c.linkedWith ? null : !editing ? (
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => openAdjust(c)}>
                  {saved ? 'Edit crop' : 'Adjust crop'}
                </button>
              ) : (
                <>
                  <select
                    className="cs-input cs-input-sm"
                    value={adjust.ratio}
                    onChange={(e) => setAdjust((a) => ({ ...a, ratio: e.target.value, size: null, sizes: {}, centers: {} }))}
                    title="Crop ratio (changing it clears drawn boxes)"
                    style={{ width: 'auto' }}
                  >
                    {CROP_RATIOS.map(([id, label]) => (
                      <option key={id} value={id}>{label === 'Original' ? 'Original ratio' : label}</option>
                    ))}
                  </select>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span className="cs-help" style={{ fontSize: 12 }}>Size:</span>
                    <button
                      className={`cs-btn cs-btn-sm ${!adjust.animate ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                      title="One crop size for the whole clip — resizing any keyframe's box resizes them all; you only re-center per keyframe"
                      onClick={() => setAdjust((a) => ({ ...a, animate: false, sizes: {} }))}
                    >
                      One per clip
                    </button>
                    <button
                      className={`cs-btn cs-btn-sm ${adjust.animate ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                      title="Keyframed crop size: each tagged frame keeps its own box size and becomes a zoom keyframe — start full frame, punch in later; use a keyframe's “◀ size / size ▶” buttons to adopt the previous/next keyframe's size"
                      onClick={() => setAdjust((a) => ({ ...a, animate: true }))}
                    >
                      ◆ Keyframed
                    </button>
                  </span>
                  <input
                    className="cs-input cs-input-sm"
                    placeholder="note (e.g. barrel bigger in frame)"
                    value={adjust.note}
                    onChange={(e) => setAdjust((a) => ({ ...a, note: e.target.value }))}
                    style={{ width: 240 }}
                  />
                  <button
                    className="cs-btn cs-btn-primary cs-btn-sm"
                    disabled={saving || !Object.keys(adjust.centers).length}
                    onClick={() => saveAdjust(c)}
                  >
                    {saving ? 'Saving…' : `Save crop (${Object.keys(adjust.centers).length})`}
                  </button>
                  <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setAdjust(null)}>Cancel</button>
                </>
              )}
            </div>
            {applyNote?.idx === c.idx && (
              <div className="cs-help" style={{ fontSize: 12, margin: '6px 0', color: applyNote.err ? 'var(--danger, #f87171)' : 'var(--surf, #4fd1c5)' }}>
                {applyNote.err || applyNote.msg}
              </div>
            )}
            {editing && (
              <div className="cs-help" style={{ fontSize: 12, margin: '6px 0' }}>
                {adjust.animate ? (
                  <>
                    ANIMATING size: each tagged frame keeps its <b>own</b> box size — they become
                    zoom keyframes, so the crop can start full frame (use “⛶ full” on a frame) and
                    smoothly punch in to a later keyframe. “◀ size / size ▶” copies the previous/next
                    keyframe’s size onto this one — keep the crop consistent where you want it, and
                    still re-center each keyframe freely. The bake eases size and center between
                    your tags with the same smoothing.
                  </>
                ) : (
                  <>
                    ONE crop size per clip: size it once (corner-drag on any frame re-zooms every
                    frame together), then “set crop” + drag on the other frames to tag where CENTER
                    belongs. YOLO subject tracking + keyframe smoothing carries the center between
                    your tagged frames in the bake. Want a push-in instead? Toggle “Animating size”.
                  </>
                )}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cropCols}, 1fr)`, gap: 6, marginTop: 8 }}>
              {(c.frames || []).map((f, j) => {
                const savedBox = !editing && saved?.boxes?.find((b) => Math.abs(b.t - f.t) < 0.05);
                // Current-in-movie crop overlay (dashed amber): full-frame
                // sheets show the ORIGINAL frame, so the live crop path is
                // drawn on top — what the bake crops to today.
                const liveBox = !editing && fullFrameSheet ? liveCropBoxAt(liveClipFor(c), f.t) : null;
                const tile = (
                  <>
                    <img
                      src={f.url}
                      alt={`clip ${c.idx} @ ${fmtTime(f.t)}`}
                      loading="lazy"
                      style={{ width: '100%', borderRadius: 4, display: 'block' }}
                      onLoad={(e) => {
                        const im = e.target;
                        if (im.naturalWidth && im.naturalHeight) setFrameAspect(im.naturalWidth / im.naturalHeight);
                      }}
                    />
                    <span
                      style={{
                        position: 'absolute', right: 4, bottom: 4, padding: '1px 6px',
                        background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, borderRadius: 3,
                      }}
                    >
                      {fmtTime(f.t)}
                    </span>
                    {liveBox && (
                      <span
                        title="Current crop in the movie edit (dashed) — Adjust crop starts from this"
                        style={{
                          position: 'absolute',
                          left: `${liveBox.x * 100}%`,
                          top: `${liveBox.y * 100}%`,
                          width: `${liveBox.w * 100}%`,
                          height: `${liveBox.h * 100}%`,
                          border: '2px dashed #f0b35e',
                          borderRadius: 2,
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                    {savedBox && (
                      <span
                        style={{
                          position: 'absolute',
                          left: `${savedBox.rect.x * 100}%`,
                          top: `${savedBox.rect.y * 100}%`,
                          width: `${savedBox.rect.w * 100}%`,
                          height: `${savedBox.rect.h * 100}%`,
                          border: '2px solid var(--surf, #4fd1c5)',
                          borderRadius: 2,
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </>
                );
                if (!editing) {
                  return (
                    <a
                      key={f.url}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      title={`Open full-res · movie ${fmtTime(f.t)}`}
                      style={{ position: 'relative', display: 'block', lineHeight: 0 }}
                    >
                      {tile}
                    </a>
                  );
                }
                const box = rectFor(adjust, j);
                return (
                  // overflow:hidden clips CropBox's outside-dim (a 9999px
                  // box-shadow) to THIS tile — without it every tagged frame
                  // stacks a 50% shade over the entire page.
                  <div key={f.url} style={{ position: 'relative', lineHeight: 0, overflow: 'hidden', borderRadius: 4 }}>
                    {tile}
                    {box ? (
                      <>
                        <CropBox
                          rect={box}
                          lockRatio={rNormFor(adjust.ratio)}
                          onChange={(r) => setAdjust((a) => {
                            // A drag moves only THIS frame's center. A corner-drag
                            // (size changed): in one-size mode it re-zooms the
                            // shared size for EVERY frame (accidental drift would
                            // make the bake breathe); in animate mode it sets THIS
                            // frame's own size — a zoom keyframe.
                            const sz = sizeFor(a, j);
                            const resized = Math.abs(r.w - sz.w) > 1e-4 || Math.abs(r.h - sz.h) > 1e-4;
                            const next = {
                              ...a,
                              centers: { ...a.centers, [j]: { cx: r.x + r.w / 2, cy: r.y + r.h / 2 } },
                            };
                            if (resized) {
                              if (a.animate) next.sizes = { ...a.sizes, [j]: { w: r.w, h: r.h } };
                              else next.size = { w: r.w, h: r.h };
                            }
                            return next;
                          })}
                        />
                        <button
                          className="cs-btn cs-btn-ghost cs-btn-sm"
                          style={{ position: 'absolute', top: 4, right: 4, zIndex: 5 }}
                          title="Remove this frame's crop tag"
                          onClick={() => setAdjust((a) => {
                            const centers = { ...a.centers };
                            const sizes = { ...a.sizes };
                            delete centers[j];
                            delete sizes[j];
                            return { ...a, centers, sizes };
                          })}
                        >
                          ×
                        </button>
                        {adjust.animate && (() => {
                          const tags = Object.keys(adjust.centers).map(Number).sort((x, y) => x - y);
                          const prevK = tags.filter((k) => k < j).pop();
                          const nextK = tags.find((k) => k > j);
                          return (
                            <span style={{ position: 'absolute', top: 4, left: 4, zIndex: 5, display: 'flex', gap: 2 }}>
                              {[
                                ['◀ size', 'previous', prevK],
                                ['size ▶', 'next', nextK],
                              ].map(([label, which, k]) => (
                                <button
                                  key={which}
                                  className="cs-btn cs-btn-sm cs-btn-primary"
                                  style={{ padding: '2px 7px', opacity: k == null ? 0.35 : 1 }}
                                  disabled={k == null}
                                  title={k == null
                                    ? `No ${which} keyframe tagged in this clip yet`
                                    : `Adopt the ${which} keyframe's crop SIZE for this one (center stays where you put it) — keeps the crop consistent across the clip while still re-centering per keyframe`}
                                  onClick={() => setAdjust((a) => ({ ...a, sizes: { ...a.sizes, [j]: { ...sizeFor(a, k) } } }))}
                                >
                                  {label}
                                </button>
                              ))}
                            </span>
                          );
                        })()}
                        <span style={{ position: 'absolute', bottom: 4, left: 4, zIndex: 5, display: 'flex', gap: 2 }}>
                          {[
                            ['⇤', 'left', (sz, ctr) => ({ ...ctr, cx: sz.w / 2 })],
                            ['⤒', 'top', (sz, ctr) => ({ ...ctr, cy: sz.h / 2 })],
                            ['⤓', 'bottom', (sz, ctr) => ({ ...ctr, cy: 1 - sz.h / 2 })],
                            ['⇥', 'right', (sz, ctr) => ({ ...ctr, cx: 1 - sz.w / 2 })],
                          ].map(([glyph, side, apply]) => (
                            <button
                              key={side}
                              className="cs-btn cs-btn-ghost cs-btn-sm"
                              style={{ padding: '2px 7px' }}
                              title={`Snap crop box flush to the ${side} edge`}
                              onClick={() => setAdjust((a) => {
                                const sz = sizeFor(a, j);
                                const ctr = a.centers[j] || { cx: 0.5, cy: 0.5 };
                                return { ...a, centers: { ...a.centers, [j]: apply(sz, ctr) } };
                              })}
                            >
                              {glyph}
                            </button>
                          ))}
                          {adjust.animate && (
                            <button
                              className="cs-btn cs-btn-ghost cs-btn-sm"
                              style={{ padding: '2px 7px' }}
                              title="Make THIS frame's crop the full frame (zoom keyframe at scale 1) — e.g. start wide, punch in later"
                              onClick={() => setAdjust((a) => {
                                const w = maxWFor(a.ratio);
                                const h = Math.min(1, w / rNormFor(a.ratio));
                                return {
                                  ...a,
                                  sizes: { ...a.sizes, [j]: { w, h } },
                                  centers: { ...a.centers, [j]: { cx: 0.5, cy: 0.5 } },
                                };
                              })}
                            >
                              ⛶ full
                            </button>
                          )}
                        </span>
                      </>
                    ) : (
                      <button
                        className="cs-btn cs-btn-ghost cs-btn-sm"
                        style={{ position: 'absolute', top: 4, left: 4, zIndex: 5 }}
                        onClick={() => setAdjust((a) => {
                          // Seed from the nearest tagged frame (center AND, in
                          // animate mode, its size) so it only needs a nudge.
                          const drawn = Object.keys(a.centers).map(Number).sort((x, y) => Math.abs(x - j) - Math.abs(y - j));
                          const prev = drawn.length ? a.centers[drawn[0]] : { cx: 0.5, cy: 0.5 };
                          const next = { ...a, size: a.size || defaultSize(a.ratio), centers: { ...a.centers, [j]: { ...prev } } };
                          if (a.animate && drawn.length && a.sizes?.[drawn[0]]) {
                            next.sizes = { ...a.sizes, [j]: { ...a.sizes[drawn[0]] } };
                          }
                          return next;
                        })}
                      >
                        + set crop
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Cell({ proof, onOpen, lookLabel }) {
  const img = sheetImg(proof);
  const baking = isBaking(proof);
  return (
    <button type="button" className="cs-sheet-cell" onClick={onOpen} title={lookLabel ? `${lookLabel} · frame ${frameOf(proof)}` : `Frame ${frameOf(proof)}`}>
      {img
        ? <img src={img} alt={`frame ${frameOf(proof)}`} style={{ opacity: baking ? 0.45 : 1 }} loading="lazy" />
        : <div className="cs-empty" style={{ width: '100%', aspectRatio: '2.13 / 1' }} />}
      {baking && <span className="cs-sheet-baking">● baking</span>}
      {lookLabel && <span className="cs-sheet-look">{lookLabel}</span>}
      <span className="cs-sheet-frame">f{frameOf(proof)}</span>
    </button>
  );
}

// Hub card for one saved movie-bake proof sheet: proper name, bake timestamp,
// and a real preview strip (each clip's first sampled frame). Mirrors the
// ClipProofs card pattern — the strip IS the proof set, not a generic icon.
function SheetCard({ sheet, onOpen }) {
  const clips = sheet.clips || [];
  const frames = clips.reduce((n, c) => n + (c.frames?.length || 0), 0);
  const crops = Object.keys(sheet.cropRequests || {}).length;
  const colorClips = Object.keys(sheet.colorProofs?.clips || {}).length;
  const colorLive = sheet.colorProofs && ['queued', 'running', 'starting'].includes(sheet.colorProofs.status);
  const when = sheet.createdAt
    ? new Date(sheet.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';
  return (
    <div className="cs-proofcard">
      <div className="cs-proofcard-head" onClick={onOpen}>
        <div style={{ minWidth: 0 }}>
          <div className="cs-proofcard-title" title={sheet.title || 'Bake proofs'}>
            {sheet.title || 'Bake proofs'}
          </div>
          <div className="cs-help" style={{ fontSize: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {sheet.movieTitle && <span className="cs-card-tag">{sheet.movieTitle}</span>}
            {when && <span>{when}</span>}
            <span>· {clips.length} clip{clips.length === 1 ? '' : 's'} · {frames} frames</span>
            {crops ? <span>· ✂ {crops} crop{crops === 1 ? '' : 's'}</span> : null}
            {colorLive
              ? <span className="cs-grab-livetag">● color proofs · {sheet.colorProofs.current || 'rendering'}</span>
              : colorClips ? <span>· 🎨 {colorClips} color-proofed</span> : null}
          </div>
        </div>
        <button className="cs-btn cs-btn-primary cs-btn-sm" style={{ flex: 'none' }} onClick={(e) => { e.stopPropagation(); onOpen(); }}>
          Open sheet →
        </button>
      </div>
      {clips.length ? (
        <div className="cs-proofstrip">
          {clips.map((c) => (
            <button
              key={c.idx}
              type="button"
              className="cs-proofstrip-thumb"
              onClick={onOpen}
              title={`Clip ${c.idx}: ${c.label || ''}`}
            >
              {c.frames?.[0]?.url
                ? <img src={c.frames[0].url} alt={`clip ${c.idx}`} loading="lazy" />
                : <div className="cs-empty" style={{ width: '100%', height: '100%' }} />}
              <span className="cs-proofstrip-frame">{c.idx}{c.red ? ' · RED' : ''}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function ProofSheet() {
  const navigate = useNavigate();
  const { docs: footage } = useAllFootage(6000);
  const { docs: trips } = useTrips();
  const [viewer, setViewer] = useState(null); // { list, index }

  // The consolidated Proofs hub. Landing = every saved movie-bake proof sheet
  // as a named/timestamped card with a real frame-strip preview; tabs pull in
  // the clip grade sessions (formerly the /proofs page) and the legacy clip
  // frame-grab sheets. Deep links keep working: ?page=<sheetId>|sessions|clips.
  const { docs: sheetDocs } = useCollection('proofSheets', { orderBy: 'createdAt' });
  const movieSheets = sheetDocs || [];
  const [params, setParams] = useSearchParams();
  const page = params.get('page') || 'sheets';
  const activeSheet = movieSheets.find((s) => s.id === page) || null;

  const tripName = useMemo(() => {
    const m = new Map((trips || []).map((t) => [t.id, t.name || t.id]));
    return (id) => m.get(id) || '';
  }, [trips]);

  // Group proof stills into sheets. A sheet is keyed by its proofSheet name (so a
  // "look test" sits apart from the clip's frame-order sheet even though both come
  // from the same clip), falling back to the source clip id.
  const groups = useMemo(() => {
    const all = (footage || []).filter((a) => a.kind === 'photo' && !isTrashed(a) && isSheet(a));
    const bySheet = new Map();
    for (const a of all) {
      const key = a.proofSheet?.name || a.stillGrab?.sourceAssetId || a.lineage?.sourceAssetId || a.proofClip || a.id;
      if (!bySheet.has(key)) bySheet.set(key, []);
      bySheet.get(key).push(a);
    }
    const list = [];
    for (const [key, proofs] of bySheet) {
      const head = proofs[0] || {};
      const lookTest = proofs.some((p) => p.lookId);
      if (lookTest) {
        // Frame-major, look-minor → each grid row is one frame across the 3 looks.
        proofs.sort((x, y) => frameOf(x) - frameOf(y) || (x.lookOrder ?? 0) - (y.lookOrder ?? 0));
      } else {
        proofs.sort((x, y) => frameOf(x) - frameOf(y));
      }
      const looks = lookTest
        ? [...new Map(proofs.map((p) => [p.lookId, { id: p.lookId, name: p.lookName, order: p.lookOrder ?? 0 }])).values()]
            .sort((a, b) => a.order - b.order)
        : [];
      list.push({
        key,
        tripId: head.tripId,
        name: head.proofSheet?.name || head.proofClip || head.lineage?.sourceName || head.stillGrab?.sourceName || 'Proof sheet',
        photographer: head.proofSheet?.photographer || '',
        context: head.proofSheet?.context || '',
        clip: head.proofSheet?.clip || head.proofClip || head.lineage?.sourceName || '',
        lookTest, looks,
        frameCount: new Set(proofs.map(frameOf)).size,
        proofs,
      });
    }
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [footage]);

  const totalProofs = groups.reduce((n, g) => n + g.proofs.length, 0);
  const bakingNow = groups.reduce((n, g) => n + g.proofs.filter(isBaking).length, 0);

  const openAt = (proofs, id) => setViewer({ list: proofs, index: Math.max(0, proofs.findIndex((p) => p.id === id)) });

  const sheetsNewest = [...movieSheets].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  const sheetWhen = activeSheet?.createdAt
    ? new Date(activeSheet.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';

  return (
    <div className="cs-section">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 6 }}>
        <div>
          <h2 style={{ margin: '0 0 2px' }}>
            {activeSheet ? (activeSheet.title || 'Bake proofs') : 'Proofs'}
            {activeSheet && sheetWhen ? <span className="cs-help" style={{ fontSize: 13, fontWeight: 400, marginLeft: 10 }}>{sheetWhen}</span> : null}
          </h2>
          <div className="cs-help" style={{ fontSize: 13 }}>
            {activeSheet ? (
              'Frames sampled from the published bake — delog, grade, reframe and crop exactly as rendered. Click a frame for full res.'
            ) : page === 'clips' ? (
              <>
                {totalProofs} proof{totalProofs === 1 ? '' : 's'} across {groups.length} clip{groups.length === 1 ? '' : 's'}
                {bakingNow ? <span className="cs-grab-livetag" style={{ marginLeft: 10 }}>● baking {bakingNow}</span> : null}
                . Three per row in frame order — click a frame to view large or open it in the editor.
              </>
            ) : page === 'sessions' ? (
              'Per-clip grade sessions: review the graded frame proofs, give feedback, authorize full bakes.'
            ) : (
              'Every proof set in one place — movie bake sheets, clip grade sessions, and clip frame grabs.'
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0 18px', alignItems: 'center' }}>
        {activeSheet ? (
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setParams({})}>
            ← All proof sets
          </button>
        ) : (
          <>
            <button
              className={`cs-btn cs-btn-sm ${page === 'sheets' ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => setParams({})}
            >
              Movie bake sheets{movieSheets.length ? ` (${movieSheets.length})` : ''}
            </button>
            <button
              className={`cs-btn cs-btn-sm ${page === 'sessions' ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => setParams({ page: 'sessions' })}
            >
              Clip grade sessions
            </button>
            <button
              className={`cs-btn cs-btn-sm ${page === 'clips' ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => setParams({ page: 'clips' })}
            >
              Clip frame grabs
            </button>
          </>
        )}
      </div>

      {activeSheet ? (
        <MovieSheetBody sheet={activeSheet} />
      ) : page === 'sessions' ? (
        <ClipProofsBody />
      ) : page === 'sheets' ? (
        !sheetsNewest.length ? (
          <div className="cs-empty">
            {sheetDocs == null
              ? <><span className="cs-spinner" /> Loading proof sheets…</>
              : 'No movie bake sheets yet — generate one after a publish (backend/scripts/proofsheet.mjs).'}
          </div>
        ) : (
          <div className="cs-proofgrid">
            {sheetsNewest.map((s) => (
              <SheetCard key={s.id} sheet={s} onOpen={() => setParams({ page: s.id })} />
            ))}
          </div>
        )
      ) : page !== 'clips' ? (
        // Deep link to a sheet id we don't have (still loading, or deleted).
        <div className="cs-empty">
          {sheetDocs == null ? <><span className="cs-spinner" /> Loading proof sheet…</> : 'Proof sheet not found.'}
        </div>
      ) : !groups.length ? (
        <div className="cs-empty">
          {footage == null ? <><span className="cs-spinner" /> Loading proofs…</> : 'No proof sheets yet. Bake a frame range tagged “proof-sheet” to populate this page.'}
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.key}>
            <div className="cs-sheet-cliptitle">
              <h3>{g.name}</h3>
              <span className="cs-help" style={{ fontSize: 12 }}>
                {tripName(g.tripId) ? `${tripName(g.tripId)} · ` : ''}
                {g.photographer ? `📷 ${g.photographer} · ` : ''}
                {g.lookTest
                  ? `${g.frameCount} frames × ${g.looks.length} looks — pick a look (one per column)`
                  : <>{g.frameCount} frame{g.frameCount === 1 ? '' : 's'} · f{frameOf(g.proofs[0])}–f{frameOf(g.proofs[g.proofs.length - 1])}</>}
              </span>
            </div>
            {g.lookTest && (
              <div className="cs-sheet-lookhead">
                {g.looks.map((l) => <div key={l.id} className="cs-sheet-lookcol">{l.name}</div>)}
              </div>
            )}
            <div className="cs-sheet-grid">
              {g.proofs.map((p) => (
                <Cell key={p.id} proof={p} lookLabel={g.lookTest ? (p.lookName || p.lookId) : ''} onOpen={() => openAt(g.proofs, p.id)} />
              ))}
            </div>
          </div>
        ))
      )}

      {viewer && (
        <PhotoLightbox
          list={viewer.list}
          index={viewer.index}
          onIndex={(i) => setViewer((v) => ({ ...v, index: i }))}
          onClose={() => setViewer(null)}
          onEdit={(a) => navigate(`/photo/${a.tripId}/${a.id}`)}
        />
      )}
    </div>
  );
}
